import { Button, Heading, Icon, Input } from "@elaniintc/goes-design-system";
import ScrollModal from "../scroll-modal";
import { useState } from "react";
import type { Editor } from "@tiptap/core";

interface Props {
  editor: Editor;
  onClose: () => void;
  onConfirm: (url: string, text: string) => void;
  onRemove?: () => void;
}

const HyperLinkModal = ({ editor, onClose, onConfirm, onRemove }: Props) => {
  const [inputValue, setInputValue] = useState(() => {
    const href = editor.getAttributes("link").href;
    return href || "";
  });
  const [textValueState, setTextValueState] = useState(() => {
    const getLinkText = () => {
      if (!editor) return "";

      // If we're inside a link, extend to get the full link text
      if (editor.isActive("link")) {
        const { from, to } = editor.state.selection;

        // Find the boundaries of the link mark
        let linkStart = from;
        let linkEnd = to;

        const $from = editor.state.doc.resolve(from);

        // Get marks at current position
        const marksAtCursor = $from.marks();
        const linkMark = marksAtCursor.find(
          (mark) => mark.type.name === "link",
        );

        if (linkMark) {
          // Scan backwards to find link start
          let pos = from;
          while (pos > 0) {
            const resolvedPos = editor.state.doc.resolve(pos - 1);
            const marks = resolvedPos.marks();
            if (
              !marks.some(
                (m) =>
                  m.type.name === "link" &&
                  m.attrs.href === linkMark.attrs.href,
              )
            ) {
              break;
            }
            pos--;
          }
          linkStart = pos;

          // Scan forwards to find link end
          pos = to;
          const docSize = editor.state.doc.content.size;
          while (pos < docSize) {
            const resolvedPos = editor.state.doc.resolve(pos);
            const marks = resolvedPos.marks();
            if (
              !marks.some(
                (m) =>
                  m.type.name === "link" &&
                  m.attrs.href === linkMark.attrs.href,
              )
            ) {
              break;
            }
            pos++;
          }
          linkEnd = pos;
        }

        return editor.state.doc.textBetween(linkStart, linkEnd);
      }

      // Not in a link, return selected text
      return editor.state.doc.textBetween(
        editor.state.selection.from,
        editor.state.selection.to,
      );
    };
    return getLinkText();
  });
  return (
    <ScrollModal
      isOpen
      onClose={onClose}
      title="Enlace"
      header={
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-center text-h2! font-semibold text-heading">
              <Heading variant="h5">Enlace</Heading>
            </div>
            <Button
              variant="quiet"
              size="xs"
              onClick={onClose}
              className="min-w-9 px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
              data-testid="close-new-notification-modal-button"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="p-6 pt-4 flex justify-between">
          <Button type="button" variant="danger-tertiary" onClick={onRemove}>
            Eliminar
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(inputValue, textValueState)}
          >
            Aceptar
          </Button>
        </div>
      }
    >
      <div className="px-6 w-full">
        <Input
          className="w-full mb-4"
          placeholder="Texto para mostrar"
          value={textValueState}
          onChange={(e) => setTextValueState(e.target.value)}
        />
        <Input
          className="w-full"
          placeholder="https://www.example.com"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>
    </ScrollModal>
  );
};

export default HyperLinkModal;
